/* ============================================================
   storage — the direct route to a drive.

   The HP drive's box runs an agent (server/nasd.py) that a browser
   on the same network — or the same tailnet, which between two
   machines on one LAN is the LAN — can send uploads to and fetch
   downloads from, instead of routing every byte through the server
   in the other house and across the home's metered link twice.

   The server says which drives have such a route FOR THIS PERSON
   (drive.direct, the agent's address) and mints a ticket for each
   transfer; this asks whether the agent actually answers from
   here. Quickly (1.5 s), once, and remembered for the session: a
   phone out on mobile data gets no answer, and everything simply
   goes the usual way.
   ============================================================ */

import { useEffect, useState } from 'react';
import { api } from './api';

const TIMEOUT_MS = 1500;
const OK_FOR = 10 * 60 * 1000;      // a yes holds for ten minutes
const NO_FOR = 60 * 1000;           // a no is asked again after one
const KEY = 'st.direct.reach';
const memo = new Map();             // base -> {ok, at}
const pending = new Map();

function remembered(base) {
  let m = memo.get(base);
  if (!m) {
    try { m = JSON.parse(window.sessionStorage.getItem(KEY) || '{}')[base]; } catch { m = null; }
  }
  if (!m) return null;
  return Date.now() - m.at < (m.ok ? OK_FOR : NO_FOR) ? m : null;
}

function remember(base, ok) {
  const m = { ok, at: Date.now() };
  memo.set(base, m);
  try {
    const all = JSON.parse(window.sessionStorage.getItem(KEY) || '{}');
    all[base] = m;
    window.sessionStorage.setItem(KEY, JSON.stringify(all));
  } catch { /* private mode: memory only */ }
}

/** Does the agent at `base` answer from this browser? */
export function reachable(base) {
  if (!base) return Promise.resolve(false);
  const m = remembered(base);
  if (m) return Promise.resolve(m.ok);
  if (pending.has(base)) return pending.get(base);
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  const p = fetch(`${base}/ping`, { signal: ctrl.signal, cache: 'no-store', mode: 'cors' })
    .then((r) => r.ok)
    .catch(() => false)
    .then((ok) => { clearTimeout(timer); remember(base, ok); pending.delete(base); return ok; });
  pending.set(base, p);
  return p;
}

/** The last answer, without asking — for code that cannot wait. */
export function reachableNow(base) {
  return !!(base && remembered(base)?.ok);
}

/** A direct transfer found the agent gone: stop offering it for a while. */
export function markUnreachable(base) {
  if (base) remember(base, false);
}

/**
 * Where the preview should fetch a file from: its drive's agent when that
 * answers here, otherwise the usual URL. `src` is null while deciding (at
 * most the probe's 1.5 s), so a player never starts on one source only to be
 * switched to the other.
 */
export function useDirectSrc(token, file, base, normalSrc, enabled = true) {
  const [state, setState] = useState({ key: null, src: null, direct: false });
  const key = `${file?.id}:${file?.sha256 || ''}:${base || ''}:${enabled}`;
  useEffect(() => {
    let live = true;
    if (!file || !base || !enabled) { setState({ key, src: normalSrc, direct: false }); return undefined; }
    setState({ key, src: null, direct: false });
    (async () => {
      let src = null;
      try {
        if (await reachable(base)) src = (await api.fileDirect(token, file.id, true)).url;
      } catch { src = null; }
      if (live) setState({ key, src: src || normalSrc, direct: !!src });
    })();
    return () => { live = false; };
    // The key covers what matters; normalSrc changes with the token only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state.key === key ? state : { src: base && enabled ? null : normalSrc, direct: false };
}
